import React from 'react';
import { MapPin, Send, Github, Instagram, Twitter, Linkedin } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { socialLinks, profileData } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const socialIcons = {
  github: Github,
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin
};

const Contact = () => {
  const { t } = useLanguage();

  const handleSubmit = (e) => {
    e.preventDefault();
    // Open email client
    window.location.href = `mailto:${profileData.email}`;
  };

  return (
    <section id="contact" className="py-32 px-6 relative bg-[hsl(270,50%,4%)]">
      {/* Background accents */}
      <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-fuchsia-600/10 rounded-full blur-[100px]" />
      
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section header */}
        <div className="text-center mb-20">
          <span className="text-purple-400 font-mono text-sm tracking-wider uppercase">{t('contact.label')}</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-6 text-white">
            {t('contact.title')} <span className="gradient-text">{t('contact.titleHighlight')}</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-fuchsia-500 mx-auto rounded-full" />
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact info */}
          <div className="space-y-8">
            <p className="text-lg text-gray-300 leading-relaxed">
              {t('contact.description')}
            </p>

            {/* Location */}
            <div className="flex items-center gap-4 p-4 rounded-xl bg-[hsl(270,40%,8%)] border border-purple-500/10">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('contact.location')}</p>
                <p className="text-gray-300">{profileData.location}</p>
              </div>
            </div>

            {/* Social links */}
            <div>
              <p className="text-gray-400 mb-4">{t('contact.social')}</p>
              <div className="flex items-center gap-4">
                {socialLinks.map((social) => {
                  const Icon = socialIcons[social.icon];
                  return (
                    <a
                      key={social.id}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-xl bg-[hsl(270,40%,8%)] border border-purple-500/10 hover:border-purple-500/50 hover:bg-purple-500/10 transition-all duration-300 group"
                      aria-label={social.name}
                    >
                      <Icon className="w-5 h-5 text-gray-400 group-hover:text-purple-400 transition-colors duration-300" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Contact form */}
          <div className="p-8 rounded-2xl bg-[hsl(270,40%,8%)] border border-purple-500/10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="text-sm text-gray-400 mb-2 block">{t('contact.form.name')}</label>
                  <Input 
                    id="contact-name"
                    name="name"
                    placeholder={t('contact.form.namePlaceholder')}
                    className="bg-[hsl(270,50%,5%)] border-purple-500/20 text-white placeholder:text-gray-500 focus:border-purple-500/50 rounded-xl h-12"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="text-sm text-gray-400 mb-2 block">{t('contact.form.email')}</label>
                  <Input 
                    id="contact-email"
                    name="email"
                    type="email"
                    placeholder={t('contact.form.emailPlaceholder')}
                    className="bg-[hsl(270,50%,5%)] border-purple-500/20 text-white placeholder:text-gray-500 focus:border-purple-500/50 rounded-xl h-12"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="contact-subject" className="text-sm text-gray-400 mb-2 block">{t('contact.form.subject')}</label>
                <Input 
                  id="contact-subject"
                  name="subject"
                  placeholder={t('contact.form.subjectPlaceholder')}
                  className="bg-[hsl(270,50%,5%)] border-purple-500/20 text-white placeholder:text-gray-500 focus:border-purple-500/50 rounded-xl h-12"
                />
              </div>
              
              <div>
                <label htmlFor="contact-message" className="text-sm text-gray-400 mb-2 block">{t('contact.form.message')}</label>
                <Textarea 
                  id="contact-message"
                  name="message"
                  placeholder={t('contact.form.messagePlaceholder')}
                  rows={5}
                  className="bg-[hsl(270,50%,5%)] border-purple-500/20 text-white placeholder:text-gray-500 focus:border-purple-500/50 rounded-xl resize-none"
                />
              </div>
              
              <Button 
                type="submit"
                className="w-full py-6 text-lg bg-purple-600 hover:bg-purple-500 text-white rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/25 group"
              >
                {t('contact.form.submit')}
                <Send className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
